import React, { Component } from 'react';
import IconButton from '@material-ui/core/IconButton';
import Badge from '@material-ui/core/Badge';
import Popover from '@material-ui/core/Popover';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Divider from '@material-ui/core/Divider';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import axios from 'axios';
import io from 'socket.io-client';
import Auth from '../auth/auth';

const auth = new Auth();
const { getAccessToken } = auth;

function authHeaders() {
  return {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    'x-access-token': getAccessToken(),
  };
}

class NotificationBell extends Component {
  constructor(props) {
    super(props);
    this.state = {
      anchorEl: null,
      notifications: [],
      unreadCount: 0,
      loading: false,
    };
    this._pollInterval = null;
    this._socket = null;
  }

  componentDidMount() {
    this.fetchUnreadCount();
    // Poll for unread count every 60 seconds (fallback if the socket drops).
    this._pollInterval = setInterval(this.fetchUnreadCount, 60000);

    // Realtime (#595): subscribe to the authenticated /workflow namespace and
    // refresh the unread count live when a Change Control notification fires.
    try {
      const socketUrl = process.env.REACT_APP_API_URL || window.location.origin;
      this._socket = io(`${socketUrl}/workflow`, {
        auth: { token: getAccessToken() },
        transports: ['websocket'],
      });
      this._socket.on('workflow:notification', () => {
        this.fetchUnreadCount();
        if (this.state.anchorEl) this.fetchNotifications();
      });
    } catch (e) {
      // Socket unavailable — polling still keeps the count fresh.
    }
  }

  componentWillUnmount() {
    if (this._pollInterval) clearInterval(this._pollInterval);
    if (this._socket) {
      this._socket.off('workflow:notification');
      this._socket.disconnect();
    }
  }

  fetchUnreadCount = () => {
    axios.get(`${process.env.REACT_APP_API_URL}/workflow/notifications/count`, { headers: authHeaders() })
      .then((r) => {
        const count = r.data && r.data.response && r.data.response.count;
        this.setState({ unreadCount: count || 0 });
      })
      .catch(() => {});
  }

  fetchNotifications = () => {
    this.setState({ loading: true });
    axios.get(`${process.env.REACT_APP_API_URL}/workflow/notifications?limit=10`, { headers: authHeaders() })
      .then((r) => {
        const rows = r.data && r.data.response && r.data.response.rows;
        this.setState({ notifications: rows || [], loading: false });
      })
      .catch(() => this.setState({ loading: false }));
  }

  handleOpen = (e) => {
    this.setState({ anchorEl: e.currentTarget });
    this.fetchNotifications();
  }

  handleClose = () => {
    this.setState({ anchorEl: null });
  }

  handleMarkAllRead = () => {
    axios.put(`${process.env.REACT_APP_API_URL}/workflow/notifications/read-all`, {}, { headers: authHeaders() })
      .then(() => {
        this.setState({ unreadCount: 0, notifications: this.state.notifications.map((n) => ({ ...n, is_read: true })) });
      })
      .catch(() => {});
  }

  handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      axios.put(`${process.env.REACT_APP_API_URL}/workflow/notifications/${notification.notification_id}/read`, {}, { headers: authHeaders() })
        .then(() => {
          this.setState((prev) => ({
            notifications: prev.notifications.map((n) =>
              n.notification_id === notification.notification_id ? { ...n, is_read: true } : n
            ),
            unreadCount: Math.max(0, prev.unreadCount - 1),
          }));
        })
        .catch(() => {});
    }
    this.handleClose();
    if (this.props.history) {
      if (notification.request_id) {
        this.props.history.push(`/change-control?request=${notification.request_id}`);
      } else if (notification.schema_id) {
        this.props.history.push(`/change-control?schema=${notification.schema_id}`);
      } else {
        this.props.history.push('/change-control');
      }
    }
  }

  render() {
    const { anchorEl, notifications, unreadCount, loading } = this.state;
    const open = Boolean(anchorEl);

    return (
      <>
        <IconButton data-testid="cc-unread-badge" onClick={this.handleOpen} style={{ color: '#fff', padding: 8 }}>
          <Badge badgeContent={unreadCount > 0 ? unreadCount : null} color="error" max={99}>
            <span className='material-icons'>notifications</span>
          </Badge>
        </IconButton>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={this.handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          PaperProps={{ style: { width: 360, maxHeight: 480 } }}
        >
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>Notifications</Typography>
            {unreadCount > 0 && (
              <Button size="small" onClick={this.handleMarkAllRead} style={{ fontSize: 12 }}>
                Mark all read
              </Button>
            )}
          </div>

          {loading ? (
            <div style={{ padding: 16, textAlign: 'center', color: '#9e9e9e', fontSize: 13 }}>Loading...</div>
          ) : notifications.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#9e9e9e', fontSize: 13 }}>No notifications</div>
          ) : (
            <List dense disablePadding style={{ maxHeight: 380, overflow: 'auto' }}>
              {notifications.map((n, i) => (
                <React.Fragment key={n.notification_id}>
                  <ListItem
                    button
                    onClick={() => this.handleNotificationClick(n)}
                    style={{ backgroundColor: n.is_read ? '#fff' : '#fff8e1', padding: '10px 16px' }}
                  >
                    <ListItemText
                      primary={
                        <span style={{ fontWeight: n.is_read ? 'normal' : 600, fontSize: 13 }}>
                          {n.title}
                        </span>
                      }
                      secondary={
                        <span style={{ fontSize: 12, color: '#757575' }}>
                          {n.message && n.message.length > 80 ? n.message.slice(0, 80) + '...' : n.message}
                          <br />
                          <span style={{ fontSize: 11, color: '#bdbdbd' }}>
                            {n.created_at ? new Date(n.created_at).toLocaleString() : ''}
                          </span>
                        </span>
                      }
                    />
                    {!n.is_read && (
                      <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ff9800', flexShrink: 0 }} />
                    )}
                  </ListItem>
                  {i < notifications.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}

          <div style={{ padding: '8px 16px', borderTop: '1px solid #e0e0e0', textAlign: 'center' }}>
            <Button
              size="small"
              color="primary"
              onClick={() => { this.handleClose(); this.props.history && this.props.history.push('/change-control'); }}
              style={{ fontSize: 12 }}
            >
              View All in Change Control →
            </Button>
          </div>
        </Popover>
      </>
    );
  }
}

export default withRouter(NotificationBell);
